import { forwardRef, ForwardedRef, ReactNode, RefObject, useRef } from "react";
import styled from "styled-components";
import { color, ColorProps, border, BorderProps } from "styled-system";
import css from "@styled-system/css";

import Icon from "../Icon/Icon";
import Box from "../Box/Box";
import Typography from "../Typography/Typography";

import { SelectProps, SelectLabelProps } from "./select.type";

const SelectContainer = styled.div`
  position: relative;
`;

const StyledSelect = styled.select<ColorProps & BorderProps & SelectProps>`
  text-overflow: ellipsis;
  &:disabled,
  &:disabled + * {
    opacity: 0.3;
    cursor: unset;
  }
  ${css({
    boxShadow: "default",
    fontSize: "body",
    border: "normalBorder",
    height: "48px",
    width: "100%",
    appearance: "none",
    cursor: "pointer",
    pl: 4,
    pr: 40,
    "&:focus": {
      outline: "none",
      border: "primaryBorder",
      boxShadow: "accent",
    },
    "&.error": {
      boxShadow: "error",
      border: "errorBorder",
      "&:focus": {
        boxShadow: "error",
        border: "errorBorder",
      },
    },
  })}
  ${color}
  ${border}
  ${(props) => props.css}
`;

const Label = ({ required, children }: SelectLabelProps) => {
  const requiredSymbol =
    children && required
      ? `&:after {
          content: "*";
          color: red;
        }`
      : "";

  return (
    <Typography
      css={`
        width: auto;
        font-weight: 500;
        ${requiredSymbol}
      `}
    >
      {children}
    </Typography>
  );
};

const Select = forwardRef(
  (
    {
      children,
      label,
      disabled = false,
      required = false,
      error = false,
      width = "auto",
      marginX = 0,
      marginY = 0,
      errorMessage,
      ...props
    }: SelectProps,
    ref: ForwardedRef<ReactNode>
  ) => {
    const internalRef = useRef<HTMLSelectElement>(null);

    return (
      <Box
        display="flex"
        flexDirection="column"
        width={width}
        marginX={marginX}
        marginY={marginY}
        role="group"
      >
        {label && <Label required={required}>{label}</Label>}
        <SelectContainer>
          <StyledSelect
            className={error ? "error" : ""}
            bg="primary-white"
            color="footer-blue"
            borderRadius={2}
            disabled={disabled}
            ref={(ref || internalRef) as RefObject<HTMLSelectElement>}
            role="listbox"
            required={required}
            aria-invalid={error}
            aria-label={label}
            {...props}
          >
            {children}
          </StyledSelect>
          <Icon
            name="angle-down"
            weight="regular"
            color={
              !error ? "cta-tertiary-text-color-default" : "negative-text-color"
            }
            width={24}
            height={24}
            css={`
              position: absolute;
              right: 14px;
              top: 14px;
              pointer-events: none;
            `}
          ></Icon>
          {error && errorMessage ? errorMessage : null}
        </SelectContainer>
      </Box>
    );
  }
);

export default Select;
